import type { Job } from '@mairistem/composants';
import type { TemplateRequestGet } from '@mairistem/finances-utils';
import { request } from '@mairistem/tools';
import type TypePeriode from '../../constants/simulation/typePeriode';
import type { ArbitrageMassePayload } from '../../interfaces/simulation/arbitrageMasse';
import type {
	default as IdeeImportable,
	ImportIdeesResultat,
	ValeursPerimetresIdee,
} from '../../interfaces/simulation/ideeImportable';
import type {
	default as MontantSimulation,
	MontantSimulationUpdate,
} from '../../interfaces/simulation/montantSimulation';
import type { ImputationLigne } from '../../utils/imputation';

const url = '/api/finances/simulation/montantSimulation';

// Imputation de la ligne (meme corps que pour upsertDemandeMontant), idees a
// importer et periode affichee : le back en a besoin pour controler les droits
// (pilote ou administrateur en Proposition et en Vote, pas d'import en fin
// d'annee)
export interface ImportIdeesPayload extends ImputationLigne {
	identifiantsIdees: number[];
	typePeriode: TypePeriode;
}

export const getMontants = (
	params?: URLSearchParams,
): Promise<TemplateRequestGet<MontantSimulation>> =>
	request.get(`${url}?${params?.toString()}`);

export const getMontantsByImputation = (
	params?: URLSearchParams,
): Promise<TemplateRequestGet<MontantSimulation>> =>
	request.get(`${url}/byImputation?${params?.toString()}`);

// Idees Vision 360 importables sur la ligne (memes parametres que
// byImputation) : deja filtrees et triees par le back, sans pagination. Toutes
// les idees eligibles sont renvoyees avec leur cran ; le front filtre selon le
// cran choisi
export const getIdeesImportables = (
	params?: URLSearchParams,
): Promise<{ data: IdeeImportable[]; perimetres: ValeursPerimetresIdee }> =>
	request.get(`${url}/ideesImportables?${params?.toString()}`);

// Cree une demande acceptee par idee importee sur la ligne. Le back relit les
// idees, reserve chacune de facon atomique (idsi_mt_simul) et renvoie les
// idees perdues lors d'un import concurrent
export const importerIdees = (data: ImportIdeesPayload) =>
	request.post<ImportIdeesPayload, ImportIdeesResultat>(
		`${url}/importIdees`,
		data,
	);

export const updateMontant = (
	montantSimulation: MontantSimulationUpdate,
	params?: URLSearchParams,
) =>
	request.put<MontantSimulationUpdate, MontantSimulation>(
		`${url}/${montantSimulation.identifiant}?${params?.toString()}`,
		montantSimulation,
	);

export const upsertMontant = (data: any) =>
	request.put<any, MontantSimulation>(`${url}/upsertByImputation`, data);

export const upsertDemandeMontant = (data: any) =>
	request.put<any, MontantSimulation>(`${url}/upsertDemandeByImputation`, data);

export const upsertMontants = (
	identifiantSimulation: number,
	montants: any,
	hasApcp: boolean,
) =>
	request.put<unknown, Job>(url, {
		montants,
		identifiantSimulation,
		hasApcp,
	});

// Arbitrage (statut) de plusieurs lignes en un seul traitement long
export const lancerArbitrageMasse = (data: ArbitrageMassePayload) =>
	request.put<ArbitrageMassePayload, Job>(`${url}/arbitrage`, data);

// La periode n'est attendue que depuis la modale d'instruction : le back en a
// besoin pour les droits de suppression d'une demande issue d'une idee
// (gestionnaire seulement en periode Demande)
export const deleteMontant = (identifiant: number, typePeriode?: TypePeriode) =>
	request.delete(
		`${url}/${identifiant}${typePeriode === undefined ? '' : `?typePeriode=${typePeriode}`}`,
	);

export const deleteMontantsApcp = (identifiantSimulation: number) =>
	request.delete(
		`${url}/montantsSimulationApcp?identifiantSimulation=${identifiantSimulation}`,
	);
